import type { AccessibilityTexts } from '../types/accessibility';
import { Eye, Ear, HandPalm, Brain, ArrowRight } from "@phosphor-icons/react";
import { formatTimer } from './utils';
// Textes d'accessibilité centralisés
export const accessibilityTexts: AccessibilityTexts = {
  // Navigation et header
  navigation: {
    mainNavigation: "Navigation principale du site",
    backToHome: "Retour à la page d'accueil d'AndyK.app",
    supportProject: "Soutenir le projet AndyK.app financièrement",
    menuButton: "Ouvrir le menu de navigation",
    skipToContent: "Aller directement au contenu principal",
    skipToFooter: "Aller directement au pied de page",
    toggleMenu: "Afficher ou masquer le menu de navigation",
    backToCourses: "Retour à la liste des parcours"
  },
  
  // Boutons et actions
  buttons: {
    startAdventure: "Commencer l'expérience immersive des parcours handicap",
    moreInfoProject: "En savoir plus sur le projet AndyK.app et ses objectifs",
    startCourse: "Démarrer ce parcours de sensibilisation au handicap",
    startMission: "Accepter et démarrer cette mission",
    accessDocument: "Télécharger le document alternatif accessible (8 ko)",
    donate: "Faire un don pour soutenir le projet via la cagnotte Teepee",
    shareNow: "Partager le site AndyK.app sur les réseaux sociaux",
    contactByEmail: "Nous contacter par email pour contribuer au projet",
    learnMore: "En savoir plus sur cette section"
  },
  
  // Images et médias
  images: {
    logo: "Logo AndyK.app représentant la mascotte Andy K.",
    heroCharacter: "Illustration d'Andy K., l'explorateur numérique, personnage principal du site",
    heroDisabilities: "Illustration représentant différents types de handicaps dans le numérique",
    alternativesIllustration: "Illustration de 3 personnages. Un avec un casque sur les oreilles dispose d'un livret pour symboliser la transcription textuelle. Un personnage avec une canne blanche et des lunettes noire dispose d'un casque pour symboliser l'audiodescription. Au milieu, un personnage sans à priori sans handicap et sans alternative.",
    donationIllustration: "Illustration représentant le concept de don et de soutien financier",
    shareIllustration: "Illustration symbolisant le partage sur les réseaux sociaux",
    profilesIllustration: "Illustration représentant les profils des créateurs du projet",
    developmentIllustration: "Illustration d'Andy K. en tant que développeur, représentant la contribution technique",
    decorativeShapes: "Formes décoratives animées en arrière-plan",
    mascotteAvatar: "Avatar de la mascotte du parcours",
    solutionCover: "Image d'illustration de la solution de la mission"
  },
  
  // Statuts et badges
  status: {
    newBadge: "Nouveau parcours récemment ajouté",
    comingSoonBadge: "Parcours en cours de développement, bientôt disponible",
    availableCourse: "Parcours disponible et accessible",
    unavailableCourse: "Parcours temporairement indisponible"
  },
  
  // Formulaires et interactions
  forms: {
    required: "Champ obligatoire",
    optional: "Champ optionnel",
    error: "Erreur dans le formulaire",
    success: "Action réalisée avec succès",
    loading: "Chargement en cours, veuillez patienter"
  },
  
  // Landmarks et régions
  landmarks: {
    mainContent: "Contenu principal de la page",
    navigation: "Navigation du site",
    banner: "Bannière du site",
    footer: "Pied de page avec liens utiles",
    coursesList: "Liste des parcours de sensibilisation disponibles",
    statisticsSection: "Section statistiques sur l'accessibilité numérique",
    helpSection: "Section d'aide et de contribution au projet"
  },
  
  // Messages d'état pour les lecteurs d'écran
  screenReader: {
    pageLoaded: "Page chargée avec succès",
    navigationSkip: "Passer au contenu principal",
    externalLink: "Lien externe",
    newWindow: "Ouvre dans un nouvel onglet",
    downloadFile: "Télécharger le fichier",
    currentPage: "Page actuelle"
  }
};

// Fonction utilitaire pour récupérer un texte d'accessibilité
export const getAccessibilityText = (key: string): string => {
  const keys = key.split('.');
  let current: any = accessibilityTexts;
  
  for (const k of keys) {
    if (current && typeof current === 'object' && k in current) {
      current = current[k];
    } else {
      console.warn(`Clé d'accessibilité introuvable: ${key}`);
      return key; // Retourne la clé comme fallback
    }
  }
  
  return typeof current === 'string' ? current : key;
};

// Hook personnalisé pour utiliser les textes d'accessibilité
export const useAccessibility = () => {
  return {
    getText: getAccessibilityText,
    texts: accessibilityTexts
  };
};

// Fonction utilitaire pour créer des attributs ARIA
export const createAriaAttributes = (
  label?: string,
  describedBy?: string,
  role?: string
) => {
  const attributes: Record<string, string> = {};
  
  if (label) attributes['aria-label'] = getAccessibilityText(label);
  if (describedBy) attributes['aria-describedby'] = describedBy;
  if (role) attributes.role = role;
  
  return attributes;
};

// Fonction pour créer des descriptions d'images accessibles
export const createImageProps = (
  src: string,
  altKey: string,
  isDecorative = false
) => {
  return {
    src,
    alt: isDecorative ? '' : getAccessibilityText(altKey),
    role: isDecorative ? 'presentation' : undefined,
    'aria-hidden': isDecorative ? 'true' : undefined
  };
};

export const createTags = (tags:string) => {
  
let data
  switch (tags.split("troubles-")[1]) {
    case "auditifs":
      data={class: "auditifs",name: "Troubles auditifs",icon: Ear}
      break;
    case "cognitifs":
      data={class: "cognitifs", name: "Troubles cognitifs", icon: Brain}
      break;
    case "intellectuels":
      data={ class: "intellectuels", name: "Troubles intellectuels", icon: Brain}
      break;
    case "visuels":
      data={ class: "visuels", name: "Troubles visuels", icon: Eye}
      break;
    case "moteurs":
      data={ class: "moteurs", name: "Troubles moteurs", icon: HandPalm}
      break;

  }
  return data!
}

export const BtnTimer = (timeLeft: number) => {
  return (
    <div
      className={`text-xl font-bold px-4 py-2 rounded-lg ${
        timeLeft <= 5
          ? 'text-red-600 bg-red-100'
          : timeLeft <= 10
          ? 'text-orange-600 bg-orange-100'
          : 'text-primary-600 bg-primary-100'
      }`}
    >
      {formatTimer(timeLeft)}
    </div>
  );
};